/**
 * Card Reader Agent status in the user UI header: the state in plain words,
 * once (audit UX-21, P7) — "Reader ready", "Reader: no SAM", "No reader" —
 * with the longer explanation in the tooltip.
 */

import { Nfc } from 'lucide-react';
import { useNfcAgent, useTranslation } from '@bms/common';
import type { NfcAgentStatus } from '@bms/common';

type Translate = ReturnType<typeof useTranslation>['t'];
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { StatusTag } from '@/components/ui/status-tag';

function statusTone(s: NfcAgentStatus): 'ready' | 'warning' | 'neutral' {
  if (s === 'connected') return 'ready';
  if (s === 'no-sam' || s === 'warning') return 'warning';
  // The agent is not running on this PC: a fact about the desk, not an alarm.
  return 'neutral';
}

function statusLabel(s: NfcAgentStatus, t: Translate): string {
  if (s === 'keymaster') return t('shell.nfcAgent.keymaster');
  if (s === 'connected') return t('shell.nfcAgent.ready');
  if (s === 'no-sam') return t('shell.nfcAgent.noSamShort');
  if (s === 'warning') return t('shell.nfcAgent.notThisSite');
  return t('shell.nfcAgent.none');
}

function statusDetail(s: NfcAgentStatus, t: Translate): string {
  if (s === 'keymaster') return t('shell.nfcAgent.keymaster');
  if (s === 'connected') return t('shell.nfcAgent.connected');
  if (s === 'no-sam') return t('shell.nfcAgent.noSam');
  if (s === 'warning') return t('shell.nfcAgent.notThisSite');
  return t('shell.nfcAgent.disconnected');
}

export function NfcAgentStatusBadge() {
  const { t } = useTranslation();
  const { status } = useNfcAgent();
  const label = statusLabel(status, t);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <StatusTag
            tone={statusTone(status)}
            size="md"
            role="status"
            className="cursor-default border border-current/20 px-2 normal-case"
          >
            <Nfc className="h-3 w-3" aria-hidden />
            {/* The phone header has room for the icon alone; the tooltip and the
                accessible name still say it. */}
            <span className="max-sm:sr-only">{label}</span>
          </StatusTag>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{statusDetail(status, t)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
